import React, { useEffect, useState } from "react";
import { 
  getOrganizations,
  createOrganization,
  updateOrganization,
  deleteOrganization,
  DEFAULT_HOURLY_RATE
} from "../api/api";
import {
  Container,
  Typography,
  List,
  Card,
  CardContent,
  CardActions,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  AppBar,
  Toolbar,
  Snackbar,
  Alert
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import AddIcon from "@mui/icons-material/Add";

const OrganizationList = () => {
  // Локальное состояние для организаций
  const [organizations, setOrganizations] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Состояние для диалогов и форм
  const [openDialog, setOpenDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [selectedOrg, setSelectedOrg] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    hourly_rate: DEFAULT_HOURLY_RATE,
  });
  
  // Состояние для уведомлений
  const [snackbar, setSnackbar] = useState({ 
    open: false, 
    message: "", 
    severity: "success" 
  });

  // Загрузка организаций при монтировании компонента
  useEffect(() => {
    fetchOrganizations();
  }, []);

  // Функция загрузки организаций
  const fetchOrganizations = async () => {
    try {
      setLoading(true);
      const response = await getOrganizations();
      const orgs = response?.data || response || [];
      setOrganizations(Array.isArray(orgs) ? orgs : []);
    } catch (error) {
      console.error("Ошибка загрузки организаций:", error);
      setSnackbar({ 
        open: true, 
        message: "Ошибка загрузки организаций", 
        severity: "error" 
      });
      setOrganizations([]);
    } finally {
      setLoading(false);
    }
  };

  // Обработчик отправки формы
  const handleSubmit = async () => {
    try {
      const payload = { 
        name: formData.name.trim(),
        hourly_rate: parseFloat(formData.hourly_rate) || DEFAULT_HOURLY_RATE
      };
      
      if (editMode && selectedOrg) {
        await updateOrganization(selectedOrg.id, payload);
        setSnackbar({ 
          open: true, 
          message: "Организация обновлена", 
          severity: "success" 
        });
      } else {
        await createOrganization(payload);
        setSnackbar({ 
          open: true, 
          message: "Организация создана", 
          severity: "success" 
        });
      }
      
      setOpenDialog(false);
      await fetchOrganizations();
      resetForm();
    } catch (error) {
      console.error("Ошибка сохранения организации:", error);
      const errorMessage = error.response?.data?.message || error.message || "Ошибка сохранения";
      setSnackbar({ 
        open: true, 
        message: errorMessage, 
        severity: "error" 
      });
    }
  };

  // Обработчик удаления организации
  const handleDelete = async () => {
    try {
      if (!selectedOrg) return;
      
      await deleteOrganization(selectedOrg.id);
      setSnackbar({ 
        open: true, 
        message: "Организация удалена", 
        severity: "success" 
      });
      setOpenDeleteDialog(false);
      setSelectedOrg(null);
      await fetchOrganizations();
    } catch (error) {
      console.error("Ошибка удаления организации:", error);
      setSnackbar({ 
        open: true, 
        message: "Ошибка удаления организации", 
        severity: "error" 
      });
    }
  };

  // Обработчик редактирования организации
  const handleEdit = (org) => {
    setEditMode(true);
    setSelectedOrg(org);
    setFormData({
      name: org.name || "",
      hourly_rate: org.hourly_rate || DEFAULT_HOURLY_RATE,
    });
    setOpenDialog(true);
  };

  // Сброс формы
  const resetForm = () => {
    setFormData({
      name: "",
      hourly_rate: DEFAULT_HOURLY_RATE,
    });
    setEditMode(false);
    setSelectedOrg(null);
  };

  // Закрытие уведомления
  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  return (
    <Container>
      <Typography variant="h4" align="center" gutterBottom sx={{ mt: 3 }}>
        Организации
      </Typography>

      <AppBar position="static" elevation={0} sx={{ backgroundColor: "transparent", mb: 3 }}>
        <Toolbar disableGutters>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => {
              resetForm();
              setOpenDialog(true);
            }}
            sx={{ ml: "auto" }}
          >
            Добавить организацию
          </Button>
        </Toolbar>
      </AppBar>

      {/* Диалог создания/редактирования */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editMode ? "Редактировать организацию" : "Новая организация"}
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Название"
            fullWidth
            value={formData.name}
            onChange={(e) => setFormData({...formData, name: e.target.value})}
            sx={{ mb: 2 }}
            required
          />
          <TextField
            margin="dense"
            label="Стоимость в час (руб.)"
            type="number"
            fullWidth
            value={formData.hourly_rate}
            onChange={(e) => setFormData({...formData, hourly_rate: e.target.value})}
            inputProps={{ step: 1, min: 0 }}
            required
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Отмена</Button>
          <Button 
            onClick={handleSubmit} 
            variant="contained"
            disabled={!formData.name.trim() || formData.hourly_rate < 0}
          >
            {editMode ? "Сохранить" : "Создать"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Состояние загрузки */}
      {loading ? (
        <Typography align="center" sx={{ py: 4 }}>
          Загрузка организаций...
        </Typography>
      ) : (
        /* Список организаций */
        <List sx={{ mb: 4 }}>
          {organizations.length === 0 ? (
            <Typography align="center" sx={{ py: 4, color: 'text.secondary' }}>
              Организации не найдены
            </Typography>
          ) : (
            organizations.map((org) => (
              <Card key={org.id} sx={{ mb: 2 }}>
                <CardContent>
                  <Typography variant="h6">{org.name}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Стоимость в час: {org.hourly_rate > 0 ? org.hourly_rate : DEFAULT_HOURLY_RATE} ₽
                  </Typography>
                </CardContent>
                <CardActions sx={{ justifyContent: "flex-end" }}>
                  <IconButton onClick={() => handleEdit(org)} title="Редактировать">
                    <EditIcon color="primary" />
                  </IconButton>
                  <IconButton 
                    onClick={() => {
                      setSelectedOrg(org);
                      setOpenDeleteDialog(true);
                    }} 
                    title="Удалить"
                  >
                    <DeleteIcon color="error" />
                  </IconButton>
                </CardActions>
              </Card>
            ))
          )}
        </List>
      )}

      {/* Диалог подтверждения удаления */}
      <Dialog open={openDeleteDialog} onClose={() => setOpenDeleteDialog(false)}>
        <DialogTitle>Подтвердите удаление</DialogTitle>
        <DialogContent>
          {selectedOrg && (
            <Typography>
              Вы уверены, что хотите удалить организацию "{selectedOrg.name}"?
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setOpenDeleteDialog(false);
            setSelectedOrg(null);
          }}>
            Отмена
          </Button>
          <Button onClick={handleDelete} color="error" variant="contained">
            Удалить
          </Button>
        </DialogActions>
      </Dialog>

      {/* Уведомления */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default OrganizationList;